/**
 * normalizeAddressText — canonicalize common address/place-name variants
 * BEFORE running the whitelist scan in useSpamProtection.
 *
 * Why a separate normalization step (vs. just adding more regex variants)?
 *   - Keeps WHITELIST_PATTERNS readable: rules can target the canonical
 *     form ("st. matthews") without enumerating every spelling.
 *   - Centralizes punctuation/abbreviation rules so they're testable in
 *     isolation and reusable by other features (e.g. lead deduping).
 *   - Applied ONLY to the text used for whitelist matching. The raw text
 *     is still scanned by SPAM_PATTERNS so we don't accidentally erase
 *     spam tells (e.g. excessive punctuation, weird casing).
 *
 * Safe by design:
 *   - Lowercases for case-insensitive matching.
 *   - Strips periods only inside known abbreviations (St., Mt., Ft., S.,
 *     So.) — never globally, so URL/email punctuation is preserved.
 *   - Collapses internal whitespace and removes hyphens between known
 *     compound place names (J-Town → j town, Nu-Lu → nulu).
 *   - Does NOT translate words → numbers, expand contractions, or touch
 *     anything outside the geography vocabulary.
 */

/** Ordered list of [matcher, replacement] applied left-to-right. Matchers
 *  run on already-lowercased text. Order matters: longer/qualified forms
 *  must come before their shorter cousins to avoid premature collapse. */
const RULES: Array<[RegExp, string]> = [
  // --- Saint / Mount / Fort: drop the period, expand short forms -----------
  // "saint matthews" → "st matthews"
  [/\bsaint\s+matthews\b/g, "st matthews"],
  [/\bst\.\s*matthews\b/g, "st matthews"],
  // "mount washington" / "mt. washington" → "mt washington"
  [/\bmount\s+washington\b/g, "mt washington"],
  [/\bmt\.\s*washington\b/g, "mt washington"],
  // "fort knox" / "ft. knox" → "ft knox"
  [/\bfort\s+knox\b/g, "ft knox"],
  [/\bft\.\s*knox\b/g, "ft knox"],

  // --- LaGrange family ------------------------------------------------------
  // "lagrange" / "la-grange" / "la grange" → "la grange"
  [/\bla[\s-]?grange\b/g, "la grange"],

  // --- J-Town family --------------------------------------------------------
  // "j-town" / "j.town" / "j town" / "jtown" → "j town"
  [/\bj[\s.\-]?town\b/g, "j town"],
  [/\bjtown\b/g, "j town"],

  // --- Cox's Creek ----------------------------------------------------------
  // "cox's creek" / "coxs creek" → "coxs creek"
  [/\bcox['']?s\s+creek\b/g, "coxs creek"],

  // --- NuLu -----------------------------------------------------------------
  // "nu-lu" / "nu lu" / "nulu" / "NULU" → "nulu"
  [/\bnu[\s-]?lu\b/g, "nulu"],

  // --- Elizabethtown shorthand ---------------------------------------------
  // "e-town" / "e town" / "etown" → "etown"
  [/\be[\s-]?town\b/g, "etown"],

  // --- PRP ------------------------------------------------------------------
  // "p.r.p." / "p r p" → "prp"
  [/\bp\.?\s*r\.?\s*p\.?\b/g, "prp"],

  // --- Southern Indiana shorthand ------------------------------------------
  // "so. indiana" / "s. indiana" / "southern ind" / "so-in" / "soin"
  // → "southern indiana"
  [/\bso\.?\s*ind(iana)?\b/g, "southern indiana"],
  [/\bs\.\s*indiana\b/g, "southern indiana"],
  [/\bsouthern\s+ind\b/g, "southern indiana"],
  [/\bso[\s-]?in\b/g, "southern indiana"],
];

/** Public entry point. Returns a string suitable for whitelist regex tests. */
export function normalizeAddressText(input: string): string {
  if (!input) return input;
  let out = input.toLowerCase();
  // Normalize curly apostrophes to straight before our cox['']?s rule runs.
  out = out.replace(/[\u2018\u2019]/g, "'");
  for (const [re, repl] of RULES) {
    out = out.replace(re, repl);
  }
  // Collapse runs of internal whitespace introduced by replacements.
  out = out.replace(/[ \t]{2,}/g, " ");
  return out;
}
